import { yupResolver } from "@hookform/resolvers/yup";
import React, { useEffect, useState, useCallback } from "react";
import { useForm } from "react-hook-form";
import * as Yup from "yup";
import { SdsForm, SmartSoftButton } from "soft_digi";
import { INVOICE_URLS } from "../../api/UserUrls";
import { useSiteContext } from "../../contexts/SiteProvider";
import { changeDateTimeZoneFormat } from "../../services/core/CommonService";
import { sumOfMultiArrayObjectsWithIndex } from "../../services/core/FilterService";
import { showAlertAutoClose } from "../../services/notifyService";
import {
  company_address_all_select,
  hubs_get_all_select,
  vendors_get_all_select,
} from "../../services/site/SelectBoxServices";
import { post } from "../../services/smartApiService";
import InvoiceSubForm from "./InvoiceAddingSubForm";
import { SdsFormElementProps } from "soft_digi/dist/sdforms/SdsFromInterFace";

interface SelectOption {
  value: any;
  label: any;
}

interface FormData {
  sd_hubs_id: SelectOption;
  sd_customer_id: SelectOption;
  sd_customer_address_id: SelectOption;
  effective_date: string;
  [key: string]: any; // For any additional fields
}

interface HeaderProps {
  loadTableData: () => void;
  id: any;
  dataIn?: any;
}

const schema = Yup.object({
  invoice_type: Yup.object().required("Invoice Type is required"),
  sd_hubs_id: Yup.object().required("Hub is required"),
  sd_customer_id: Yup.object().required("Customer is required"),
  sd_customer_address_id: Yup.object().required("Billing Address is required"),
});

const InvoiceAddingForm: React.FC<HeaderProps> = ({
  loadTableData,
  id,
  dataIn = {},
}) => {
  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<any>({
    defaultValues: dataIn,
    resolver: yupResolver(schema),
  });

  const [allHubs, setAllHubs] = useState<SelectOption[]>([]);
  const [allVendors, setAllVendors] = useState<SelectOption[]>([]);
  const [custAddress, setCustAddress] = useState<SelectOption[]>([]);
  const { closeModal } = useSiteContext();
  const [types, setTypes] = useState<any[]>([]);

  const fetchInitialData = useCallback(async () => {
    try {
      hubs_get_all_select((data: SelectOption[]) => setAllHubs(data));
      vendors_get_all_select((data: SelectOption[]) => setAllVendors(data));
    } catch (error) {
      console.error("Error fetching initial data:", error);
      showAlertAutoClose("Failed to load initial data", "error");
    }
  }, []);

  useEffect(() => {
    fetchInitialData();
  }, [fetchInitialData]);

  useEffect(() => {
    const subscription = watch((value: any, { name }) => {
      if (name === "sd_customer_id" && value.sd_customer_id) {
        const customerId = value.sd_customer_id.value;
        if (customerId && customerId > 0) {
          company_address_all_select(customerId, (data: SelectOption[]) =>
            setCustAddress(data)
          );
        }
      }
    });
  }, [watch]);

  const onSubmit = (data: any) => {
    const url = INVOICE_URLS.INSERT_MANUAL;
    const data_in = {
      ...data,

      bill_id: id,
    };

    const subscription = post(url, data_in).subscribe(() => {
      closeModal();
      loadTableData();
    });
    return () => subscription.unsubscribe();
  };

  const formElements: SdsFormElementProps[] = [
    {
      type: "SELECT_BOX",
      width: 6,
      name: "invoice_type",
      element: {
        label: "Invoice Type",
        isRequired: true,
        options: [
          { value: 2, label: "Manual Invoice" },
          { value: 3, label: "Credit Note" },
        ],
        inputType: "BORDER_LABEL",
        disabled: !!dataIn.ID,
      },
    },
    {
      type: "SELECT_BOX",
      width: 6,
      name: "sd_hubs_id",
      element: {
        label: "Hub ID",
        isRequired: true,
        options: allHubs,
        inputType: "BORDER_LABEL",
        disabled: !!dataIn.ID,
      },
    },
    {
      type: "SELECT_BOX",
      width: 6,
      name: "sd_customer_id",
      element: {
        label: "Customer Name",
        isRequired: true,
        options: allVendors,
        inputType: "BORDER_LABEL",
        disabled: !!dataIn.ID,
      },
    },
    {
      type: "SELECT_BOX",
      width: 6,
      name: "sd_customer_address_id",
      element: {
        label: "Billing Address",
        isRequired: true,
        options: custAddress,
        inputType: "BORDER_LABEL",
        disabled: !!dataIn.ID,
      },
    },

    {
      type: "LABEL",
      name: "label_date",
      width: 12,
      labelComponent: (
        <InvoiceSubForm
          errors={errors}
          control={control}
          // watch={watch}
          setValue={setValue}
        />
      ),
    },
  ];

  const footerCount = useCallback(() => {
    if (!dataIn.sub_data) return null;

    return (
      <table className="smart-table-column-width-100">
        <tbody>
          <tr>
            {types.map((obj: any, key: number) => {
              const _total_count = sumOfMultiArrayObjectsWithIndex(
                dataIn.sub_data || [],
                "sub_data",
                "ID",
                obj.ID
              );
              return (
                <td
                  key={`foot_count_${key}`}
                  className="smart-table-column-width-20 has-text-centered"
                >
                  {_total_count}
                </td>
              );
            })}
          </tr>
        </tbody>
      </table>
    );
  }, [types, dataIn.sub_data]);

  const footerComponent = (
    <tfoot>
      <tr>
        {/* <td colSpan={2} className="has-text-right">
          Total Count
        </td> */}
        <td>{footerCount()}</td>
      </tr>
    </tfoot>
  );

  return (
    <div className="sd-efl-input">
      <SdsForm elements={formElements} errors={errors} control={control} />

      <div className="columns"></div>

      {footerComponent}

      <div className="has-text-right">
        <SmartSoftButton
          label="Cancel"
          classList={["button", "mt-4", "mr-4", "smart-third-button"]}
          onClick={closeModal}
        />
        <SmartSoftButton
          label={dataIn.ID ? "Update" : "Submit"}
          rightIcon="fa fa-arrow-right"
          classList={["button", "mt-4", "smart-action-button"]}
          onClick={handleSubmit(onSubmit)}
        />
      </div>
    </div>
  );
};

export default InvoiceAddingForm;
